import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, formatApiError, tokenStore } from '@/lib/api';
import { passkeyService } from '@/services/account.service';
import { startAuthentication } from '@/lib/webauthn';
import { withCurrentLocale } from '@/lib/locale';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const IMPERSONATOR_STASH = 'sila_impersonator_stash';

// Rotta di atterraggio per ruolo/tenant (coerente con LoginScreen.destFor).
function destFor(u) {
  if (!u) return '/area';
  if (u.role === 'admin') return '/admin';
  if (u.role === 'customer') return '/cliente';
  if (u.role === 'staff') return '/staff-area';
  if (u.role === 'investor') return '/investitori';
  if (u.role === 'stakeholder' && u.tenant_type === 'guida') return '/area/parco';
  return '/area';
}

export function AuthProvider({ children }) {
  // undefined = checking, null = not authenticated, object = user
  const [user, setUser] = useState(undefined);
  const [token, setToken] = useState(() => tokenStore.getAccess());

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password, code) => {
    const { data } = await api.post('/auth/login', { email, password, ...(code ? { code } : {}) });
    // Audience per-scheda in base al ruolo: consente sessioni multi-ruolo simultanee.
    tokenStore.setAudience(data.user?.role === 'customer' ? 'customer' : 'staff');
    // Persistiamo i token: garantisce la sessione anche su host senza cookie (HTTP/IP).
    tokenStore.set(data.access_token, data.refresh_token);
    setToken(data.access_token || null);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    return data;
  };

  const customerRegister = async (payload) => {
    const { data } = await api.post('/customer/register', payload);
    // Nuovo flusso: l'account richiede verifica email (nessuna sessione finché non è verificata).
    if (!data.access_token) return data;
    tokenStore.setAudience('customer');
    tokenStore.set(data.access_token, data.refresh_token);
    setToken(data.access_token || null);
    setUser(data.user);
    return data;
  };

  // Verifica email con OTP: su successo (customer) apre la sessione operativa.
  const verifyEmail = async (email, code) => {
    const { data } = await api.post('/auth/verify-email', { email, code });
    if (data.access_token) {
      tokenStore.setAudience(data.role === 'customer' ? 'customer' : 'staff');
      tokenStore.set(data.access_token, data.refresh_token);
      setToken(data.access_token || null);
      setUser(data.user);
    }
    return data;
  };
  const resendEmailOtp = async (email) => (await api.post('/auth/verify-email/resend', { email })).data;

  // Login passwordless con Passkey (WebAuthn). `email` opzionale: se omessa usa
  // le credenziali discoverable (l'utente sceglie la passkey dal prompt del browser).
  const loginWithPasskey = async (email) => {
    const { options, state } = await passkeyService.loginBegin(email);
    const assertion = await startAuthentication(options);
    const data = await passkeyService.loginComplete({ state, credential: assertion });
    tokenStore.setAudience(data.user?.role === 'customer' ? 'customer' : 'staff');
    tokenStore.set(data.access_token, data.refresh_token);
    setToken(data.access_token || null);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (e) { /* ignore */ }
    tokenStore.clear();
    try { localStorage.removeItem(IMPERSONATOR_STASH); } catch { /* noop */ }
    setUser(null);
    setToken(null);
  };

  // ------------------- Impersonation ("Entra come") -------------------
  // L'admin conserva i propri token in uno "stash" e assume la sessione del
  // target. Un reload garantisce la reinizializzazione pulita di tutti i context
  // (Realtime/Cart) con il nuovo token.
  const impersonate = async (uid, reason) => {
    tokenStore.setAudience('staff');
    const stash = { access: tokenStore.getAccess(), refresh: tokenStore.getRefresh() };
    try { localStorage.setItem(IMPERSONATOR_STASH, JSON.stringify(stash)); } catch { /* noop */ }
    try {
      const { data } = await api.post(`/admin/impersonate/${uid}`, { reason: reason || '' });
      tokenStore.set(data.access_token, data.refresh_token);
      setToken(data.access_token || null);
      // Redirect localizzato: evita il doppio salto /admin -> /es/admin della guardia
      // lingua (che causava un lampo di pagina bianca al "cambio sessione").
      window.location.assign(withCurrentLocale(destFor(data.user)));
      return data.user;
    } catch (e) {
      try { localStorage.removeItem(IMPERSONATOR_STASH); } catch { /* noop */ }
      throw e;
    }
  };

  const stopImpersonation = async () => {
    try { await api.post('/admin/impersonate/session/stop'); } catch { /* ignore */ }
    let stash = null;
    try { stash = JSON.parse(localStorage.getItem(IMPERSONATOR_STASH) || 'null'); } catch { /* noop */ }
    tokenStore.setAudience('staff');
    if (stash && stash.access) {
      tokenStore.set(stash.access, stash.refresh);
    } else {
      tokenStore.clear();
    }
    try { localStorage.removeItem(IMPERSONATOR_STASH); } catch { /* noop */ }
    // Redirect localizzato (vedi sopra): niente lampo bianco al ritorno dall'impersonazione.
    window.location.assign(withCurrentLocale('/admin/stakeholders'));
  };

  const value = {
    user, token, login, register, customerRegister, verifyEmail, resendEmailOtp, logout,
    loginWithPasskey,
    refreshUser: fetchMe, formatApiError,
    impersonate, stopImpersonation,
    isImpersonating: !!(user && user.impersonated),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
